import {
  MinusCircleIcon,
  InfoCircleIcon,
  Building02Icon,
  CheckIcon,
  Phone01Icon,
  SwitchHorizontal01Icon,
  Building07Icon,
  Building08Icon,
} from '@untitledui-icons/react/line'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipTrigger } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'
import type { OutcomeType } from '@/types/conversation'

interface OutcomeBadgeProps {
  outcome: OutcomeType
  showLabel?: boolean
  className?: string
}

const outcomeIcons: Record<OutcomeType, React.ComponentType<{ className?: string }>> = {
  no_action: MinusCircleIcon,
  information_provided: InfoCircleIcon,
  business_follow_up: Building02Icon,
  completed: CheckIcon,
  caller_follow_up: Phone01Icon,
  transferred: SwitchHorizontal01Icon,
  answered_by_business: Building07Icon,
  missed_by_business: Building08Icon,
}

const outcomeColors: Record<OutcomeType, { text: string; bg: string; border: string }> = {
  no_action: {
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-200 dark:border-green-800',
  },
  information_provided: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-200 dark:border-blue-800',
  },
  business_follow_up: {
    text: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-950',
    border: 'border-orange-200 dark:border-orange-800',
  },
  completed: {
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-200 dark:border-green-800',
  },
  caller_follow_up: {
    text: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-950',
    border: 'border-indigo-200 dark:border-indigo-800',
  },
  transferred: {
    text: 'text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
  },
  answered_by_business: {
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  missed_by_business: {
    text: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-950',
    border: 'border-rose-200 dark:border-rose-800',
  },
}

const outcomeTranslationKeys: Record<OutcomeType, string> = {
  no_action: 'resources.conversations.outcomes.no_action',
  information_provided: 'resources.conversations.outcomes.information_provided',
  business_follow_up: 'resources.conversations.outcomes.business_follow_up',
  completed: 'resources.conversations.outcomes.completed',
  caller_follow_up: 'resources.conversations.outcomes.caller_follow_up',
  transferred: 'resources.conversations.outcomes.transferred',
  answered_by_business: 'resources.conversations.outcomes.answered_by_business',
  missed_by_business: 'resources.conversations.outcomes.missed_by_business',
}

export function OutcomeBadge({ outcome, showLabel = false, className }: OutcomeBadgeProps) {
  const { t } = useTranslation()
  const Icon = outcomeIcons[outcome] || InfoCircleIcon
  const colors = outcomeColors[outcome] || outcomeColors.no_action
  const label = t(outcomeTranslationKeys[outcome] ?? 'resources.conversations.outcomes.none')

  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium cursor-default',
        colors.text,
        colors.bg,
        colors.border,
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {showLabel && <span>{label}</span>}
    </span>
  )

  if (!showLabel) {
    return (
      <TooltipTrigger>
        {badge}
        <Tooltip>{label}</Tooltip>
      </TooltipTrigger>
    )
  }

  return badge
}

export default OutcomeBadge
